import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";
import moment from "moment";
import Roboto from "../../../assets/fonts/Roboto/Roboto-Regular.ttf";
import RobotoBold from "../../../assets/fonts/Roboto/Roboto-Bold.ttf";

Font.register({
  family: "Roboto",
  fonts: [{ src: Roboto }, { src: RobotoBold, fontWeight: 600 }],
  format: "truetype",
});

const styles = StyleSheet.create({
  section: {
    display: "flex",
    width: "49.5%",
    border: "1px solid #ddd",
    padding: 5,
    borderRadius: 5,
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
  },
  item: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    margin: 5,
    marginLeft: 0,
    fontSize: 12,
  },
  page: {
    padding: 30,
    color: "#333",
    pageBreakAfter: "always",
    boxSizing: "border-box",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottom: "1px solid #ddd",
    paddingBottom: 5,
  },
  logoContainer: {
    width: "20%",
    marginRight: 10,
  },
  logo: {
    width: 100,
    height: 35,
  },
  logoPlaceholder: {
    width: 80,
    height: 80,
    marginRight: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f5f5f5",
    borderRadius: 4,
  },
  companyInfo: {
    flex: 1,
    textAlign: "left",
  },
  companyName: {
    fontSize: 14,
    fontWeight: 600,
    textTransform: "capitalize",
    marginBottom: 5,
    letterSpacing: 1,
  },
  companyAddress: {
    fontSize: 10,
    color: "#555",
    lineHeight: 1.5,
  },

  label: {
    width: 90,
    fontSize: 8,
    color: "#555",
  },

  colon: {
    width: 8,
    fontSize: 8,
    textAlign: "center",
  },

  value: {
    flex: 1,
    fontSize: 8,
    textAlign: "left",
    color: "#000",
  },
});

const PayslipData = ({ payslipData }) => {
  const renderSection = (title, data, total) =>
    data ? (
      <View style={styles.section}>
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            margin: 5,
            marginLeft: 0,
            marginTop: 0,
            marginBottom: 0,
            borderBottom: "1px dotted #ddd",
          }}
        >
          <Text style={{ fontSize: 9, fontWeight: 600, marginBottom: 3 }}>
            {title}
          </Text>
          <Text style={{ fontSize: 9, fontWeight: 600, marginBottom: 3 }}>
            Amount
          </Text>
        </View>
        {data?.map((item) => (
          <View style={styles.item} key={item.name}>
            <Text style={{ fontSize: 8 }}>{item.component_name}</Text>
            <Text style={{ fontSize: 8 }}>Rs. {item.amount}</Text>
          </View>
        ))}
        <View style={styles.item}>
          <Text style={{ fontSize: 8, fontWeight: 600 }}>Total {title}</Text>
          <Text style={{ fontSize: 8, fontWeight: 600 }}>Rs. {total}</Text>
        </View>
      </View>
    ) : null;

  return (
    <Document
      title={`${
        payslipData?.user_name + payslipData?.pay_schedule_name || "payslip"
      }`}
    >
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          {payslipData?.company_logo_url ? (
            <View style={styles.logoContainer}>
              <Image style={styles.logo} src={payslipData.company_logo_url} />
            </View>
          ) : (
            <View style={styles.logoPlaceholder}></View>
          )}

          <View style={styles.companyInfo}>
            <Text style={styles.companyName}>
              {payslipData?.company_name || "Organflow"}
            </Text>

            {payslipData?.company_address && (
              <Text style={styles.companyAddress}>
                {payslipData?.company_address}
              </Text>
            )}
          </View>
        </View>

        <View
          style={{
            marginTop: 10,
            marginBottom: 3,
            alignItems: "center",
          }}
        >
          <Text
            style={{
              fontSize: 10,
              fontWeight: 600,
            }}
          >
            Employee Pay Summary of{" "}
            {moment(payslipData?.pay_date, "DD-MM-YYYY").format("MMMM, YYYY")}
          </Text>
        </View>

        <View
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            marginTop: 5,
          }}
        >
          <View
            style={{
              display: "flex",
              width: "49.5%",
              padding: 5,
              borderRadius: 5,
              boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
            }}
          >
            <View style={styles.item}>
              <Text style={styles.label}>Employee Name</Text>
              <Text style={styles.colon}>:</Text>
              <Text style={styles.value}>{payslipData?.user_name}</Text>
            </View>
            <View style={styles.item}>
              <Text style={styles.label}>Designation</Text>
              <Text style={styles.colon}>:</Text>
              <Text style={styles.value}>
                {payslipData?.user_designation || "N/A"}
              </Text>
            </View>
            <View style={styles.item}>
              <Text style={styles.label}>Pay Date</Text>
              <Text style={styles.colon}>:</Text>
              <Text style={styles.value}>{payslipData?.pay_date || "N/A"}</Text>
            </View>
            <View style={styles.item}>
              <Text style={styles.label}>Pay Period</Text>
              <Text style={styles.colon}>:</Text>
              <Text style={styles.value}>
                {moment(payslipData?.pay_date, "DD-MM-YYYY").format(
                  "MMMM-YYYY"
                )}
              </Text>
            </View>
          </View>

          <View
            style={{
              display: "flex",
              width: "49.5%",
              padding: 5,
              borderRadius: 5,
              boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
            }}
          >
            <View style={styles.item}>
              <Text style={styles.label}>Monthly Salary</Text>
              <Text style={styles.colon}>:</Text>
              <Text style={styles.value}>
                {payslipData?.monthly_salary
                  ? payslipData?.monthly_salary
                  : "N/A"}
              </Text>
            </View>
            <View style={styles.item}>
              <Text style={styles.label}>Total Shift Payable</Text>
              <Text style={styles.colon}>:</Text>
              <Text style={styles.value}>
                {payslipData?.total_payable_shift || 0}
              </Text>
            </View>
            <View style={styles.item}>
              <Text style={styles.label}>Net Payable Shift</Text>
              <Text style={styles.colon}>:</Text>
              <Text style={styles.value}>
                {payslipData?.net_payable_shift || 0}
              </Text>
            </View>
            <View style={styles.item}>
              <Text style={styles.label}>Net Salary</Text>
              <Text style={styles.colon}>:</Text>
              <Text style={styles.value}>
                {payslipData?.net_pay ? payslipData?.net_pay : "N/A"}
              </Text>
            </View>
          </View>
        </View>

        <View
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            marginTop: 5,
          }}
        >
          {renderSection(
            "Earnings",
            payslipData?.earnings,
            payslipData?.total_earnings
          )}
          {renderSection(
            "Deductions",
            payslipData?.deduction,
            payslipData?.total_deductions
          )}
        </View>

        <View
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            marginTop: 5,
          }}
        >
          {renderSection(
            "Other Earnings",
            payslipData?.other_earnings,
            payslipData?.total_other_earnings
          )}
          {renderSection(
            "Other Deductions",
            payslipData?.other_deduction,
            payslipData?.total_other_deductions
          )}
        </View>

        <View
          style={{
            marginTop: 5,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            padding: 5,
            backgroundColor: "#84deb8",
            borderRadius: 5,
          }}
        >
          <Text style={{ fontSize: 9, fontWeight: 600 }}>
            Total Net Payable
          </Text>
          <Text style={{ fontSize: 9, fontWeight: 600 }}>
            Rs. {payslipData?.net_pay}
          </Text>
        </View>

        <View
          style={{
            marginBottom: 5,
            padding: 5,
          }}
        >
          <Text
            style={{
              fontSize: 8,
            }}
          >
            Total Net Payable: Rs. {payslipData?.net_pay} (Rupees{" "}
            {payslipData?.net_pay_in_words})
          </Text>
        </View>

        <Text style={{ textAlign: "center", marginTop: 3, fontSize: 8 }}>
          --- This is a system generated payslip ---
        </Text>
      </Page>
    </Document>
  );
};

export default PayslipData;
